import { useMemo, useState } from 'react';
import { TableProps } from '@mui/material';
import { HeaderTable, TableColumnOrder } from '@/types';
import { getComparator } from '@/utils/order';
import CustomTable from './CustomTable.component';

interface CustomTableProps<CustomType> extends TableProps {
  data: CustomType[];
  rowRender: (row: CustomType, index?: number) => JSX.Element;
  headers?: React.ReactNode;
  headersArray?: HeaderTable<CustomType>[];
  initialOrderBy: keyof CustomType;
  toolbar?: React.ReactNode;
}

const CustomTableContainer = function <CustomType>({
  data,
  rowRender,
  headers,
  headersArray,
  initialOrderBy,
  toolbar,
  ...props
}: CustomTableProps<CustomType>): JSX.Element {
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [order, setOrder] = useState<TableColumnOrder>('desc');
  const [orderBy, setOrderBy] = useState<keyof CustomType>(initialOrderBy);

  const handleChangePage = (event: unknown, newPage: number): void => {
    setCurrentPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(0);
  };

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof CustomType
  ): void => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const emptyRowsQuantity = useMemo(() => {
    const emptyRows =
      currentPage > 0 ? Math.max(0, (1 + currentPage) * rowsPerPage - data.length) : 0;

    return emptyRows;
  }, [currentPage, data.length, rowsPerPage]);

  const startSlice = currentPage * rowsPerPage;
  const endSlice = currentPage * rowsPerPage + rowsPerPage;

  const renderRows = data
    .sort(getComparator(order, orderBy))
    .slice(startSlice, endSlice)
    .map(rowRender);

  return (
    <CustomTable
      dataLength={data.length}
      currentPage={currentPage}
      rowsPerPage={rowsPerPage}
      emptyRowsQuantity={emptyRowsQuantity}
      headers={headers}
      headersArray={headersArray}
      orderBy={orderBy}
      order={order}
      toolbar={toolbar}
      handleChangePage={handleChangePage}
      handleChangeRowsPerPage={handleChangeRowsPerPage}
      handleRequestSort={handleRequestSort}
      {...props}
    >
      {renderRows}
    </CustomTable>
  );
};

export default CustomTableContainer;
